import type { ReactNode } from 'react'
import { Sequence } from 'remotion'
import type { TimelinePiece } from '../utils/mediaTimeline'

/**
 * Plays one media element per timeline piece, back to back (#89).
 *
 * A single piece renders `children` bare — the exact tree `ClipScene` and
 * `VideoLayout` had before #89 — so a scene with nothing cut is unchanged.
 * Several pieces each get a `<Sequence layout="none">`, which keeps the media a
 * direct child of the caller's container. The default absolute-fill wrapper is
 * NOT neutral: verified on a local render, it reflowed `VideoLayout`'s
 * `objectFit: contain` video to full-bleed. Remotion refuses `premountFor`
 * with `layout="none"`, so the Player may buffer briefly at a seam; renders
 * are unaffected.
 */
export function TimelineMedia({
  pieces,
  children,
}: {
  pieces: readonly TimelinePiece[]
  children: (piece: TimelinePiece, index: number) => ReactNode
}) {
  if (pieces.length === 1) return <>{children(pieces[0]!, 0)}</>

  return (
    <>
      {pieces.map((piece, index) => (
        <Sequence
          durationInFrames={piece.durationInFrames}
          from={piece.from}
          key={`${index}:${piece.trimBefore}`}
          layout="none"
        >
          {children(piece, index)}
        </Sequence>
      ))}
    </>
  )
}
